import { useMemo } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { ConstrainedTimeGraphProps } from './StockBuild.types'
import { Loader } from 'Common/Loader/Loader'
import { format } from 'date-fns'

export const ConstrainedTimeGraph = ({
  isLoading,
  xAxisData,
  yAxisDataOne,
  yAxisDataTwo,
  yAxisDataThree,
  yAxisDataFour,
  yAxisDataFive,
  traceOneName,
  traceTwoName,
  traceThreeName,
  traceFourName,
  traceFiveName,
}: ConstrainedTimeGraphProps) => {
  const hovertemplate = () => {
    return `%{text}<br>` + `<extra></extra>`
  }

  const hoverTextCompiled: string[] | undefined | (number | undefined)[] =
    useMemo(() => {
      try {
        const hoursIncreased = yAxisDataThree?.map((data: number) => {
          if (data > 0) {
            return traceThreeName
          }
          return ''
        })

        const hoursDecreased = yAxisDataFour?.map((data: number) => {
          if (data > 0) {
            return traceFourName
          }
          return ''
        })

        const hoursUnChanged = yAxisDataFive?.map((data: number) => {
          if (data > 0) {
            return traceFiveName
          }
          return ''
        })

        return xAxisData?.map((dateText: string, index: number) => {
          const hourDifference =
            hoursIncreased[index] ||
            hoursDecreased[index] ||
            hoursUnChanged[index]

          const barHeightOfTheWeek =
            yAxisDataThree[index] ||
            yAxisDataFour[index] ||
            yAxisDataFive[index]
          const workCentreCapacity = yAxisDataOne[index]
          const unconstrainedHour = yAxisDataTwo[index]
          const dateAndWeekText = `${format(
            new Date(dateText),
            'dd MMM yyy'
          )} (Week ${index + 1})`

          return (
            `   <b>${hourDifference ?? ''}</b><br>` +
            `   <b>Week:  </b>${dateAndWeekText}<br>` +
            `   <b>Work centre capacity:  </b>${workCentreCapacity}<br>` +
            `   <b>Unconstrained hours:  </b>${unconstrainedHour}<br>` +
            `   <b>Constrained hours:  </b>${barHeightOfTheWeek}<br>`
          )
        })
      } catch (e) {
        console.log('error', e)
        return new Array(xAxisData?.length).fill('-')
      }
    }, [
      xAxisData,
      yAxisDataOne,
      yAxisDataTwo,
      yAxisDataThree,
      yAxisDataFour,
      yAxisDataFive,
      traceFiveName,
      traceFourName,
      traceThreeName,
    ])

  const data = useMemo(() => {
    const trace1 = {
      x: xAxisData,
      y: yAxisDataOne,
      name: traceOneName,
      fill: 'tozeroy',
      type: 'scatter',
      textfont: { color: 'transparent' },
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#dedeef',
      },
    }

    const trace2 = {
      x: xAxisData,
      y: yAxisDataTwo,
      type: 'scatter',
      textfont: { color: 'transparent' },
      name: traceTwoName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#6f6fce',
      },
    }

    const trace3 = {
      x: xAxisData,
      y: yAxisDataThree,
      type: 'bar',
      textfont: { color: 'transparent' },
      name: traceThreeName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#00a2e0',
      },
    }

    const trace4 = {
      x: xAxisData,
      y: yAxisDataFour,
      type: 'bar',
      textfont: { color: 'transparent' },
      name: traceFourName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#d467ac',
      },
    }

    const trace5 = {
      x: xAxisData,
      y: yAxisDataFive,
      type: 'bar',
      textfont: { color: 'transparent' },
      name: traceFiveName,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      marker: {
        color: '#d8d8d8',
      },
    }

    return [trace1, trace2, trace3, trace4, trace5]
  }, [
    xAxisData,
    yAxisDataOne,
    yAxisDataTwo,
    yAxisDataThree,
    yAxisDataFour,
    yAxisDataFive,
    hoverTextCompiled,
    traceFiveName,
    traceFourName,
    traceOneName,
    traceThreeName,
    traceTwoName,
  ])

  const layout = {
    height: 280,
    barmode: 'stack',
    xaxis: {
      tickformat: '%d %b %y',
      nticks: 10,
    },
    yaxis: {
      title: {
        text: 'Total time',
        standoff: 5,
      },
      showgrid: true,
    },
    legend: {
      orientation: 'h',
      traceorder: 'normal',
      y: 1.2,
    },
    margin: {
      l: 70,
      r: 30,
      b: 50,
      t: 50,
    },
  }
  if (isLoading) {
    return <Loader />
  }
  return <Graph data={data} layout={layout} />
}
